import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { 
  Shield, CheckCircle, XCircle, AlertTriangle, RefreshCw, 
  TrendingUp, TrendingDown, Minus, ChevronRight, Loader2,
  Activity, Target, AlertCircle, Clock, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  LineChart, Line, XAxis, YAxis, Tooltip, AreaChart, Area,
  BarChart, Bar, CartesianGrid
} from 'recharts';

const COLORS = {
  passing: '#10B981',
  failing: '#EF4444',
  warning: '#F59E0B',
  neutral: '#6B7280'
};

export default function ComplianceDashboardPage() {
  const { user, viewingOrg } = useContext(AuthContext);
  const isSuperAdmin = user?.role === 'super_admin';
  
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState(null);
  const [failingControls, setFailingControls] = useState(null);
  const [trend, setTrend] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  useEffect(() => {
    fetchComplianceData();
  }, [viewingOrg]);

  const fetchComplianceData = async () => {
    setLoading(true);
    try {
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      
      const [scoreRes, categoryRes, failingRes, trendRes] = await Promise.all([
        axios.get(`${API}/compliance/score${params}`),
        axios.get(`${API}/compliance/score/category${params}`),
        axios.get(`${API}/compliance/failing${params}`),
        axios.get(`${API}/compliance/score/trend${params}`)
      ]);
      
      setScore(scoreRes.data);
      setCategoryBreakdown(categoryRes.data.categories);
      setFailingControls(failingRes.data);
      setTrend(trendRes.data.trend);
    } catch (error) {
      console.error('Failed to fetch compliance data:', error);
      // Initialize with empty data if no score exists yet
      if (error.response?.status === 400) {
        setScore(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCollectEvidence = async () => {
    setSyncing(true);
    try {
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.post(`${API}/evidence/collect${params}`);
      toast.success('Evidence collection started');
      
      // Wait a bit then refresh
      setTimeout(() => {
        fetchComplianceData();
        setSyncing(false);
      }, 3000);
    } catch (error) {
      console.error('Evidence collection failed:', error);
      toast.error('Failed to collect evidence');
      setSyncing(false);
    }
  };

  const handleEvaluateControls = async () => {
    setEvaluating(true);
    try {
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      
      // First evaluate, then calculate score
      await axios.post(`${API}/evidence/evaluate${params}`);
      await axios.post(`${API}/compliance/calculate${params}`);
      
      toast.success('Compliance evaluation completed');
      await fetchComplianceData();
    } catch (error) {
      console.error('Evaluation failed:', error);
      toast.error('Failed to evaluate controls');
    } finally {
      setEvaluating(false);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // No score yet - show setup prompt
  if (!score || !score.overall_score) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
              Compliance Automation
            </h1>
            <p className="text-slate-600 mt-1">
              Automated compliance monitoring and scoring
            </p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Set Up Compliance Automation
          </h2>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            Connect your integrations (M365, AWS, etc.) and run your first compliance evaluation 
            to see your automated compliance score.
          </p>
          
          <div className="flex justify-center gap-4">
            <Button
              onClick={handleCollectEvidence}
              disabled={syncing}
              variant="outline"
            >
              {syncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Collecting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Collect Evidence
                </>
              )}
            </Button>
            <Button
              onClick={handleEvaluateControls}
              disabled={evaluating}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {evaluating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Evaluating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Run Evaluation
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const pieData = [
    { name: 'Passing', value: score.passing_controls, color: COLORS.passing },
    { name: 'Failing', value: score.failing_controls, color: COLORS.failing }
  ];

  const categoryData = categoryBreakdown ? 
    Object.entries(categoryBreakdown).map(([name, data]) => ({
      name: name.length > 15 ? name.substring(0, 15) + '...' : name,
      fullName: name,
      score: data.score,
      passing: data.passing,
      failing: data.failing,
      total: data.total
    })).sort((a, b) => a.score - b.score) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
            Compliance Automation
          </h1>
          <p className="text-slate-600 mt-1">
            Continuous compliance monitoring powered by automated evidence collection
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleCollectEvidence}
            disabled={syncing}
            variant="outline"
          >
            {syncing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Collecting...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Collect Evidence
              </>
            )}
          </Button>
          <Button
            onClick={handleEvaluateControls}
            disabled={evaluating}
            className="bg-slate-900 text-white hover:bg-slate-800"
          >
            {evaluating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Evaluating...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Evaluate Now
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Overall Score Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Score */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-sm font-medium text-slate-600 mb-1">Overall Compliance Score</h2>
              <div className="flex items-baseline gap-2">
                <span className={`text-5xl font-bold ${getScoreColor(score.overall_score)}`}>
                  {score.overall_score?.toFixed(0)}%
                </span>
                {score.score_history && score.score_history.length > 1 && (
                  <span className="flex items-center text-sm">
                    {score.score_history[score.score_history.length - 1]?.score > 
                     score.score_history[score.score_history.length - 2]?.score ? (
                      <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                    ) : score.score_history[score.score_history.length - 1]?.score < 
                        score.score_history[score.score_history.length - 2]?.score ? (
                      <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                    ) : (
                      <Minus className="w-4 h-4 text-slate-400 mr-1" />
                    )}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {score.passing_controls} of {score.total_controls} controls passing
              </p>
            </div>
            
            <div className="w-32 h-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={50}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Score Trend */}
          {trend && trend.length > 1 && (
            <div className="mt-6 pt-4 border-t border-slate-100">
              <h3 className="text-sm font-medium text-slate-600 mb-3">30-Day Trend</h3>
              <div className="h-24">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend}>
                    <defs>
                      <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <Area 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#10B981" 
                      fill="url(#scoreGradient)"
                      strokeWidth={2}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value}%`, 'Score']}
                      labelFormatter={(label) => new Date(label).toLocaleDateString()}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Passing Controls</p>
                <p className="text-2xl font-bold text-green-600">{score.passing_controls}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-200" />
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Failing Controls</p>
                <p className="text-2xl font-bold text-red-600">{score.failing_controls}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-200" />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Critical Failures</p>
                <p className="text-2xl font-bold text-red-700">{score.critical_failing || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-200" />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Last Evaluated</p>
                <p className="text-sm font-medium text-slate-700">
                  {score.calculated_at ? new Date(score.calculated_at).toLocaleString() : 'Never'}
                </p>
              </div>
              <Clock className="w-8 h-8 text-slate-200" />
            </div>
          </div>
        </div>
      </div>

      {/* Framework Scores */}
      {score.framework_scores && Object.keys(score.framework_scores).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Framework Compliance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(score.framework_scores).map(([framework, fwScore]) => (
              <div key={framework} className="border border-slate-100 rounded-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-900">{framework}</span>
                  <span className={`text-lg font-bold ${getScoreColor(fwScore.score_percentage)}`}>
                    {fwScore.score_percentage?.toFixed(0)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${getScoreBgColor(fwScore.score_percentage)} transition-all`}
                    style={{ width: `${fwScore.score_percentage}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-2">
                  <span>{fwScore.passing_controls} passing</span>
                  <span>{fwScore.failing_controls} failing</span>
                </div>
                {fwScore.score_change !== undefined && fwScore.score_change !== 0 && (
                  <div className="flex items-center mt-2 text-xs">
                    {fwScore.score_change > 0 ? (
                      <span className="text-green-600 flex items-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        +{fwScore.score_change}%
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        {fwScore.score_change}%
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {categoryData.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Compliance by Category</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" domain={[0, 100]} unit="%" />
                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value}% (${props.payload.passing}/${props.payload.total})`,
                    props.payload.fullName
                  ]}
                />
                <Bar dataKey="score" fill="#3B82F6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Failing Controls by Severity */}
      {failingControls && (failingControls.CRITICAL?.length > 0 || failingControls.HIGH?.length > 0) && (
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Priority Remediations</h2>
          
          <div className="space-y-4">
            {/* Critical */}
            {failingControls.CRITICAL?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Critical ({failingControls.CRITICAL.length})
                </h3>
                <div className="space-y-2">
                  {failingControls.CRITICAL.slice(0, 3).map((control, idx) => (
                    <div key={idx} className="bg-red-50 border border-red-100 rounded-sm p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-red-900">{control.name}</p>
                          <p className="text-sm text-red-700 mt-1">{control.summary}</p>
                          {control.remediation && (
                            <p className="text-xs text-red-600 mt-2">
                              <strong>Fix:</strong> {control.remediation}
                            </p>
                          )}
                        </div>
                        <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded">
                          {control.category}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* High */}
            {failingControls.HIGH?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-orange-700 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  High ({failingControls.HIGH.length})
                </h3>
                <div className="space-y-2">
                  {failingControls.HIGH.slice(0, 3).map((control, idx) => (
                    <div key={idx} className="bg-orange-50 border border-orange-100 rounded-sm p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-orange-900">{control.name}</p>
                          <p className="text-sm text-orange-700 mt-1">{control.summary}</p>
                          {control.remediation && (
                            <p className="text-xs text-orange-600 mt-2">
                              <strong>Fix:</strong> {control.remediation}
                            </p>
                          )}
                        </div>
                        <span className="text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded">
                          {control.category}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
